'use client';

import type { CategoryDto } from '@/queries/categories/categories.schema';
import type { RootState } from '@/redux/store';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { FastShopFlowController } from '@/components/features/fast-shop/FastShopFlowController';
import { SkeletonParent } from '@/components/ui/Skeleton/skeleton';
import Toast from '@/components/ui/Toast/toast';
import { AppConfig } from '@/consts/AppConfig';
import { useCurrency } from '@/hooks/useCurrency';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsThisPage } from '@/hooks/useIsThisPage';
import openModal from '@/modals/utils/openModal';
import { useGameLaunchUrl } from '@/queries/games/game.mutations';
import { DEFAULT_GAME_QUERY_OPTIONS, gameQueryOptions } from '@/queries/games/games.queries';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { useWalletMeQuery } from '@/queries/wallet/wallet.queries';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import { useRestrictedGamePromoOnlyPopup } from '../_hooks/useRestrictedGamePromoOnlyPopup';
import { getGameLaunchBalanceGateAction } from '../_utils/gameLaunchBalanceGate';
import { GameIframe } from './GameIframe';
import PageBreadCrumb from './PageBreadCrumb';

type GameViewProps = {
  id: string;
  isSlotsView?: boolean;
};

// TODO: Need to transfer this somewhere else later, now it's commonly used component for game and slot pages
const GameView = ({ id, isSlotsView }: GameViewProps) => {
  const t = useTranslations('game');
  const isMobile = useIsMobile();
  const { currency, setCurrency } = useCurrency();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isFullscreen: isMobileFullscreen,
    toggleFullscreen: toggleMobileFullscreen,
  } = useFullscreen(containerRef);

  const [isDesktopFullscreen, setIsDesktopFullscreen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string>();
  const canLaunchInGcAfterGateRef = useRef(false);
  const didGateSwitchToGcRef = useRef(false);

  const { data: game, isLoading: isGameLoading } = useQuery(gameQueryOptions.one(id, DEFAULT_GAME_QUERY_OPTIONS.client));
  const { data: user } = useUserMeQuery();
  const { data: walletData, isLoading: isWalletLoading } = useWalletMeQuery();

  const { mutate: getLaunchUrl } = useGameLaunchUrl();

  const { userSession, loading: isUserSessionLoading } = useAppSelector((state: RootState) => state.user);
  // TODO: need to figure out better way how to handle from where user came from to show correct category in breadcrumb, because now we just take first active category which is not good
  const category = useMemo(() => game?.categories.find(c => c.isActive), [game]);
  const isDesktopFullscreenAndLoaded = isDesktopFullscreen && !isGameLoading && !isWalletLoading;
  const isGamePage = useIsThisPage('/game/');
  const shouldShowKycReminderModal = user?.isKycVerified !== true && (user?.verificationState === 'not-submitted' || user?.verificationState === 'incomplete');

  useRestrictedGamePromoOnlyPopup({
    gameId: id,
    isGameSessionActive: Boolean(iframeUrl),
    currency,
    sweepCoin: walletData?.sweepCoin,
    bonusSweepCoin: walletData?.bonusSweepCoin,
    isBonusAllowed: game?.isBonusAllowed,
  });

  const handleFullscreenToggle = () => {
    if (isMobile) {
      toggleMobileFullscreen();
    } else {
      const next = !isDesktopFullscreen;
      setIsDesktopFullscreen(next);

      window.dispatchEvent(new CustomEvent('app:fullscreen', {
        detail: next,
      }));
    }
  };

  useEffect(() => {
    // INFO:  For Android to avoid not clickable game in fullscreen;
    if (isMobile) {
      if (isMobileFullscreen) {
        document.body.classList.add('pointer-events-auto!');
      } else {
        document.body.classList.remove('pointer-events-auto!');
      }
    }
  }, [isMobileFullscreen, isMobile]);

  const handleGameStart = (currency: string) => {
    const canLaunchInGcAfterGate = currency === 'GC' && canLaunchInGcAfterGateRef.current;
    if (shouldShowKycReminderModal && !canLaunchInGcAfterGate) {
      openModal('kyc-not-submitted', { source: (walletData?.goldCoin && walletData?.goldCoin > 0) || currency === 'SC' ? 'game' : undefined });
      return;
    }
    canLaunchInGcAfterGateRef.current = false;

    const launchBalanceAction = getGameLaunchBalanceGateAction({
      currency,
      goldCoin: walletData?.goldCoin,
      sweepCoin: walletData?.sweepCoin,
      bonusSweepCoin: walletData?.bonusSweepCoin,
      isBonusAllowed: game?.isBonusAllowed,
    });

    if (launchBalanceAction === 'promo-disallowed') {
      openModal('disallow-game-by-category');
      return;
    }

    if (launchBalanceAction === 'deposit') {
      const isAddSource = isGamePage;
      openModal('deposit', isAddSource
        ? { source: 'game' }
        : undefined);
      return;
    }

    getLaunchUrl(
      {
        gameId: id,
        userId: user?.id || '',
        currency,
        language: 'en_SC',
        country: AppConfig.country,
        device: isMobile ? 'mobile' : 'desktop',
      },
      {
        onSuccess: (data) => {
          setIframeUrl(data.url);
        },
        onError: () => {
          Toast({
            type: 'error',
            title: t('failed-to-launch-the-game'),
          });
        },
      },
    );
  };

  const isGameStartEnabled = useMemo(() => {
    return currency && user && !isWalletLoading;
  }, [currency, user, isWalletLoading]);

  useEffect(() => {
    if (isGameStartEnabled) {
      handleGameStart(currency);
    }

    if (!userSession && !isUserSessionLoading) {
      openModal('login');
    }

    // change dependencies carrefully
  }, [currency, id, isGameStartEnabled, userSession, isUserSessionLoading]);

  useEffect(() => {
    const handleLaunchInGcAfterGate = () => {
      canLaunchInGcAfterGateRef.current = true;
      didGateSwitchToGcRef.current = true;
    };

    window.addEventListener('game:launch-gc-after-kyc-gate', handleLaunchInGcAfterGate);
    return () => {
      window.removeEventListener('game:launch-gc-after-kyc-gate', handleLaunchInGcAfterGate);
    };
  }, []);

  // On leaving the game page after a KYC-gated GC launch, restore the switcher to SC
  // so the KYC popup shows again on the next launch until KYC is completed.
  useEffect(() => {
    return () => {
      if (didGateSwitchToGcRef.current) {
        setCurrency('SC');
      }
    };
  }, [setCurrency]);

  useEffect(() => {
    if (isDesktopFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDesktopFullscreen]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('game:started', { detail: Boolean(iframeUrl) }));
    return () => {
      window.dispatchEvent(new CustomEvent('game:started', { detail: false }));
    };
  }, [iframeUrl]);

  return (
    <>
      <FastShopFlowController />
      {!isDesktopFullscreen && !isGameLoading
        ? (
            <>
              <PageBreadCrumb
                category={category as CategoryDto}
                gameName={game?.name}
                gameId={id}
              />
              {!isSlotsView && <h1 className="sr-only">{game?.name}</h1>}
            </>
          )
        : (
            <SkeletonParent className="h-7 w-75 mb-3.5 max-md:hidden" />
          )}
      {isDesktopFullscreenAndLoaded && (
        <div className="min-h-112.5 max-md:h-full h-131"></div>
      )}

      {!isGameLoading && !isWalletLoading
        ? (
            <div
              ref={containerRef}
              className={cn(
                'flex flex-col bg-grayscale-main-900 w-full text-white relative -mt-0.5',
                isMobileFullscreen && 'fixed inset-0 z-9999 max-lg:rounded-none',
                isDesktopFullscreen ? 'absolute inset-0 z-10 mt-0 rounded-none 3xl:my-5 3xl:ml-10 3xl:max-w-312 max-h-[calc(100dvh-4rem)] overflow-hidden' : 'min-h-134.25 md:h-153.75 rounded-2xl',
              )}
            >
              <div className={cn('flex justify-end items-center bg-black overflow-clip p-2', isDesktopFullscreen || isMobileFullscreen ? 'rounded-none' : 'rounded-t-2xl')}>
                <button
                  type="button"
                  className="text-c200 flex items-center gap-2 w-fit transition-colors duration-200"
                  onClick={handleFullscreenToggle}
                >
                  <p className="text-xs text-white font-medium">
                    {t('game-view')}
                  </p>
                  <Icon name={isDesktopFullscreen || isMobileFullscreen ? 'close' : 'fullScreen'} className="size-6 text-white" />
                </button>
              </div>

              {iframeUrl
                ? (
                    <GameIframe
                      src={iframeUrl}
                      game={game}
                      className={cn('w-full grow', isDesktopFullscreen || isMobileFullscreen ? 'rounded-none' : 'rounded-b-2xl')}
                    />
                  )
                : null}
            </div>
          )
        : (
            <SkeletonParent className="w-full min-h-153.7 md:h-153.75 rounded-3xl lg:rounded-2xl" />
          )}

    </>
  );
};

export default GameView;
